import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  ChevronDown,
  ChevronRight,
  FileLock2,
  FileText,
  GripVertical,
} from "lucide-react";
import { useMemo, useState } from "react";
import { NavLink, useParams } from "react-router-dom";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { groupKeys } from "@/features/groups/group-queries";
import { useFirebase } from "@/hooks/use-firebase";
import { buildPageTree, type PageTreeNode } from "@/lib/pages/page-tree";
import { validatePageMove } from "@/lib/pages/page-tree";
import { cn } from "@/lib/utils";
import { movePage } from "@/services/page-service";
import type { DocumentationPage } from "@/types/domain";
import { CreatePageButton } from "./create-page-button";

function TreeItem({
  node,
  groupId,
  expanded,
  toggle,
}: {
  node: PageTreeNode;
  groupId: string;
  expanded: Set<string>;
  toggle: (id: string) => void;
}) {
  const sortable = useSortable({ id: node.id });
  const isExpanded = expanded.has(node.id);
  const hasChildren = node.children.length > 0;
  return (
    <div
      ref={sortable.setNodeRef}
      style={{
        transform: CSS.Transform.toString(sortable.transform),
        transition: sortable.transition,
        opacity: sortable.isDragging ? 0.45 : 1,
      }}
    >
      <div
        className="group/tree flex items-center"
        style={{ paddingLeft: `${Math.min(node.depth, 4) * 12}px` }}
      >
        <button
          type="button"
          onClick={() => toggle(node.id)}
          className={cn(
            "grid size-6 shrink-0 place-items-center rounded text-slate-400 hover:bg-slate-200",
            !hasChildren && "invisible",
          )}
          aria-label={isExpanded ? "Collapse subpages" : "Expand subpages"}
        >
          {isExpanded ? (
            <ChevronDown className="size-3.5" />
          ) : (
            <ChevronRight className="size-3.5" />
          )}
        </button>
        <button
          type="button"
          className="invisible grid size-5 shrink-0 cursor-grab place-items-center rounded text-slate-400 group-hover/tree:visible active:cursor-grabbing"
          aria-label={`Move ${node.title}`}
          {...sortable.attributes}
          {...sortable.listeners}
        >
          <GripVertical className="size-3.5" />
        </button>
        <NavLink
          to={`/app/groups/${groupId}/pages/${node.id}`}
          className={({ isActive }) =>
            cn(
              "flex min-w-0 flex-1 items-center gap-2 rounded-lg px-2 py-1.5 text-sm text-slate-600 hover:bg-slate-200/70 hover:text-slate-950",
              isActive && "bg-indigo-50 font-semibold text-indigo-700",
            )
          }
        >
          <span className="text-sm">{node.icon || "📄"}</span>
          <span className="truncate">{node.title || "Untitled"}</span>
          {node.permissionMode === "restricted" && (
            <FileLock2 className="ml-auto size-3 text-amber-500" />
          )}
          {node.status === "draft" && (
            <span className="ml-auto rounded bg-slate-200 px-1 text-[9px] uppercase text-slate-500">
              draft
            </span>
          )}
        </NavLink>
        {node.depth < 4 && (
          <span className="invisible group-hover/tree:visible">
            <CreatePageButton
              groupId={groupId}
              parentPageId={node.id}
              label="Add subpage"
              variant="ghost"
              size="icon"
            />
          </span>
        )}
      </div>
      {isExpanded &&
        node.children.map((child) => (
          <TreeItem
            key={child.id}
            node={child}
            groupId={groupId}
            expanded={expanded}
            toggle={toggle}
          />
        ))}
    </div>
  );
}

export function PageTree({
  pages,
  groupId,
}: {
  pages: DocumentationPage[];
  groupId: string;
}) {
  const params = useParams();
  const { functions } = useFirebase();
  const queryClient = useQueryClient();
  const storageKey = `master-notes:expanded:${groupId}`;
  const [expanded, setExpanded] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(storageKey) ?? "[]") as string[];
      return new Set([...stored, ...(params.pageId ? [params.pageId] : [])]);
    } catch {
      return new Set<string>();
    }
  });
  const tree = useMemo(() => buildPageTree(pages), [pages]);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  const toggle = (id: string) => {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      localStorage.setItem(storageKey, JSON.stringify([...next]));
      return next;
    });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    if (!event.over || event.active.id === event.over.id) return;
    const moving = pages.find((page) => page.id === event.active.id);
    const target = pages.find((page) => page.id === event.over!.id);
    if (!moving || !target) return;

    const sameParent = moving.parentPageId === target.parentPageId;
    const destination = sameParent
      ? target.parentPageId
        ? pages.find((page) => page.id === target.parentPageId) ?? null
        : null
      : target;
    const descendants = pages.filter((page) => page.ancestorIds.includes(moving.id));
    const relativeDepth = descendants.reduce(
      (max, page) => Math.max(max, page.depth - moving.depth),
      0,
    );
    const validation = validatePageMove(moving, destination, relativeDepth);
    if (!validation.valid) {
      toast.error(validation.reason);
      return;
    }

    void movePage(
      functions,
      groupId,
      moving.id,
      sameParent ? target.parentPageId : target.id,
      target.order,
    )
      .then(() =>
        queryClient.invalidateQueries({ queryKey: groupKeys.pages(groupId) }),
      )
      .catch((error: unknown) =>
        toast.error(error instanceof Error ? error.message : "Could not move page"),
      );
  };

  if (!pages.length) {
    return (
      <div className="rounded-xl border border-dashed border-slate-300 p-4 text-center">
        <FileText className="mx-auto size-5 text-slate-400" />
        <p className="mt-2 text-xs leading-5 text-slate-500">
          No pages yet. Start with a blank page.
        </p>
        <CreatePageButton
          groupId={groupId}
          label="Create page"
          variant="ghost"
          size="sm"
        />
      </div>
    );
  }

  return (
    <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
      <SortableContext
        items={pages.map((page) => page.id)}
        strategy={verticalListSortingStrategy}
      >
        <div className="space-y-0.5">
          {tree.map((node) => (
            <TreeItem
              key={node.id}
              node={node}
              groupId={groupId}
              expanded={expanded}
              toggle={toggle}
            />
          ))}
          <CreatePageButton
            groupId={groupId}
            label="New root page"
            variant="ghost"
            size="sm"
          />
        </div>
      </SortableContext>
    </DndContext>
  );
}
